
import { Component, Prop, Vue } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import { ShareWithPerson, EventDetails, AuditRecordDTO, EventRecord } from "@/store/site-monitor/types";
import { DeviceService } from "@sureview/camera";
import { formatDateMixin } from "@/mixins";
import Multiselect from "vue-multiselect";
import vSelect3 from "vselect3";
import { validationMixin } from "vuelidate";
import { required, minLength } from "vuelidate/lib/validators";
import { SessionResource, UpdateSessionParams } from "@/store/sessions/types";
import api from "@/services/api.service";
import { debounce } from "lodash";
import { PaginatedSearchQueryParams } from "@/store/types";
import { PaginatedSearchRequest } from "@/mixins/PaginatedSearch";
import axios, { CancelTokenSource } from "axios";
import { IntegrationTemplateContactType } from "@/store/tasks/notification/IntegrationTemplateContactType";
import { IntegrationTemplateContact } from "@/store/tasks/notification/IntegrationTemplateContact";
import { IntegrationTemplate } from "@/store/tasks/notification/IntegrationTemplate";
import ContactGroupMembership from "@/types/sv-data/contact-groups/ContactGroupMembership";
import GenericTable, { TableHeader } from "@/components/table/generic-table.vue";
import EventRecordTypes from '@/types/sv-data/enums/EventRecordTypes';

const Tasks = namespace("tasks");
const SiteMonitor = namespace("siteMonitor");
const SessionStore = namespace("sessions");
const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;


const { displayDateFromISOString } = formatDateMixin.methods;

export interface MassNotificationRecipient {
	ExternalID: string;
	Email: string;
	Fullname: string;
	Telephone: string;
}

@Component({
	components: {
		multiselect: Multiselect,
		"v-select-3": vSelect3,
		"generic-table": GenericTable,
	},
	mixins: [validationMixin],
	validations: {
		template: {
			subject: {
				required,
			},
			message: {
				required,
			},
		},
		notificationRecipients: {
			required,
			minLength: minLength(1),
		},
	},
})
export default class NotificationTask extends Vue {
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;
	@Prop(String) taskText: string;
	@Prop(Object) taskData: object;
	@Prop(String) result: string;

	@SessionStore.Action updateSession: (params: UpdateSessionParams) => Promise<void>;
	@SessionStore.Getter getSession: (resourceId: number) => string;

	@SiteMonitor.Action createAuditRecord: (request: AuditRecordDTO) => Promise<EventRecord>;
	@SiteMonitor.Action eventShareSearch: (SearchTerm: string) => Promise<void>;
	@SiteMonitor.Getter("getEventDetails") eventDetails: EventDetails;

	private columns: TableHeader[] = [
		{
			title: "Full Name",
			key: "fullName",
			order: 1,
			sortOrder: 0,
			sortOrderReversed: false,
			description: "The contacts full name",
			searchable: true,
			visible: true,
			dataType: "string",
			isTermLabel: true,
			sortable: false,
			isSortedByDefault: false,
		},
		{
			title: "Email",
			key: "email",
			order: 3,
			sortOrder: 0,
			sortOrderReversed: false,
			description: "The contacts email",
			searchable: false,
			visible: true,
			dataType: "string",
			isTermLabel: true,
			sortable: false,
			isSortedByDefault: false,
		},
	];
	private contactGroupMembers: ContactGroupMembership[] = [];
	private contactGroupSearchToken: CancelTokenSource | null = null;
	private defaultPageSize: number = 20;
	private IntegrationContact: IntegrationTemplateContactType = IntegrationTemplateContactType.Contact;
	private IntegrationContactAdhoc: IntegrationTemplateContactType = IntegrationTemplateContactType.Adhoc;
	private IntegrationContactGroup: IntegrationTemplateContactType = IntegrationTemplateContactType.ContactGroup;
	private isSearching: boolean = false;
	private isSending: boolean = false;
	private notificationName: string | null = null;
	private readonly debouncePeriod: number = process.env.NODE_ENV === "test" ? 1 : 500;
	private searchedContacts: IntegrationTemplateContact[] = [];
	private selectedItem: IntegrationTemplateContact | null = null;
	private selectedUsers: IntegrationTemplateContact[] = [];
	private serverId: number | null = null;
	private showAdvanced: boolean = false;
	private showUserSearch: boolean = false;
	private template: IntegrationTemplate = {
		integrationTemplateId : null,
		serverId : null,
		groupId : null,
		title : null,
		subject : null,
		message	: null,
		syncIdentifier : null,
		markDeletedAt	: null,
		createdAt : null,
		users : [],
		contactGroups : [],
		contactGroupMembers : [],
		parameters : null,
		groupSyncId : null
	};
	private templateContactSearchToken: CancelTokenSource | null = null;
	private templateId: number | null = null;
	private templateUsers: IntegrationTemplateContact[] = [];

	/**
	 * Lifecycle hook called when the component is created.
	 * @returns {Promise<void>}
	 */
	private async created(): Promise<void> {
		await this.updateSession({ resourceId: SessionResource.DeviceServiceSession });
	}

	/**
	 * Lifecycle hook called when the component is mounted.
	 * @returns {Promise<void>}
	 */
	private async mounted(): Promise<void> {
		if (this.taskData && this.taskData["title"] && this.taskData["serverId"] && this.taskData["templateId"]) {
			this.notificationName = this.taskData["title"];
			this.serverId = this.taskData["serverId"];
			this.templateId = this.taskData["templateId"];
		}

		if (this.templateId) {
			const loadedTemplate = await api.loadIntegrationTemplateDetails(this.templateId);
			
			if (loadedTemplate) {
				this.template = loadedTemplate;
				this.initializeTemplateUsers();
				if (this.isContactGroupsEnabled) {
					this.initializeContactGroups();
				}
			}
		}
	}

	/**
	 * Initializes template users.
	 * @returns {void}
	 */
	private initializeTemplateUsers(): void {
		if(this.template && this.template.users){
			const users = this.template!.users.map(
			(x) =>
				({
					contactGroupId: null,
					contactType: IntegrationTemplateContactType.Contact,
					default: true,
					email: x.email,
					externalId: x.externalID,
					fullName: x.fullName,
					telephone: x.telephone,
					userId: x.userID,
				} as IntegrationTemplateContact)
			);

			// add the contact groups to the template users list
			this.templateUsers = [...this.templateUsers, ...users];
		}
	}

	/**
	 * Initializes contact groups.
	 * @returns {void}
	 */
	private initializeContactGroups(): void {
		if(this.template && this.template.contactGroups){
			const contactGroups: IntegrationTemplateContact[] = this.template!.contactGroups.map(
			(x) =>
				({
					contactGroupId: x.contactGroupId,
					contactType: IntegrationTemplateContactType.ContactGroup,
					default: true,
					email: null,
					externalId: null,
					fullName: x.title,
					telephone: null,
					userId: null,
				} as IntegrationTemplateContact)
		);

		// add the contact groups to the template users list
		this.templateUsers = [...this.templateUsers, ...contactGroups];

		const contactGroupMembers: ContactGroupMembership[] = this.template!.contactGroupMembers.map((x) => ({
			contactGroupIds: x.contactGroupIds,
			email: x.email,
			fullName: x.fullName,
			userId: x.userId,
		}));

		// add the contact group members to the contact group members list
		this.contactGroupMembers = [...this.contactGroupMembers, ...contactGroupMembers];
	
		}
	}

	/**
	 * Gets all the users that should be included in this notification including template and manually selected users.
	 * @returns {IntegrationTemplateContact[]}
	 */
	private get notificationRecipients(): IntegrationTemplateContact[] {
		return this.templateUsers.concat(this.selectedUsers);
	}

	/**
	 * Checks if contact groups feature is enabled.
	 * @returns {boolean}
	 */
	private get isContactGroupsEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "NotificationGroups"]);
	}

	/**
	 * Retrieves contact groups based on the search term.
	 * @param {string} searchTerm - The search term for contact groups.
	 * @returns {Promise<IntegrationTemplateContact[]>}
	 */
	private async retrieveContactGroups(searchTerm: string = ""): Promise<IntegrationTemplateContact[]> {
		if (!searchTerm || searchTerm.length == 0) {
			return [];
		}

		let contactGroups = [];

		const params: PaginatedSearchQueryParams = {
			page: 1,
			pageSize: 50,
			sortBy: "title",
			searchTerm: searchTerm,
		};

		this.contactGroupSearchToken = axios.CancelToken.source();

		const request: PaginatedSearchRequest = {
			params: params,
			cancelToken: this.contactGroupSearchToken.token,
		};

		try {
			contactGroups = (await api.getContactGroups(request)).data;
		} catch (err) {
			console.error(err);
			this.$notify({
				type: "error",
				title: "Retrieving Contact Groups",
				text: err.message,
			});
			return [];
		}

		return contactGroups.map(
			(cg) =>
				({
					contactGroupId: cg.contactGroupId,
					fullName: cg.title,
					contactType: IntegrationTemplateContactType.ContactGroup,
				} as IntegrationTemplateContact)
		);
	}

	/**
	 * Removes a contact from the list of recipients.
	 * @param {IntegrationTemplateContact} contact - The contact to be removed.
	 */
	private removeUser(contact: IntegrationTemplateContact): void {
		if (contact.contactType == this.IntegrationContact) {
			if (contact.userId !== null) {
				this.templateUsers = this.templateUsers.filter((x) => x.userId !== contact.userId);
				this.selectedUsers = this.selectedUsers.filter((x) => x.userId !== contact.userId);
			}
		}

		if (contact.contactType == this.IntegrationContactGroup) {
			this.templateUsers = this.templateUsers.filter((x) => x.contactGroupId !== contact.contactGroupId);
			this.selectedUsers = this.selectedUsers.filter((x) => x.contactGroupId !== contact.contactGroupId);
			this.contactGroupMembers = this.contactGroupMembers.filter((f) => !f.contactGroupIds.includes(contact.contactGroupId));
		}

		if (contact.contactType == this.IntegrationContactAdhoc && contact.email !== null) {
			this.selectedUsers = this.selectedUsers.filter((x) => x.email !== contact.email);
		}
	}

	/**
	 * Replaces keyword placeholders in the notification message with actual values.
	 * @param {string} message - The message containing keywords.
	 * @returns {string}
	 */
	private replaceKeywords(message: string): string {
		if (typeof message !== 'string' || !message) {
			return '';
		}

		if (!Array.isArray(this.availablePlaceholders) || this.availablePlaceholders.length === 0) {
			return message;
		}

		this.availablePlaceholders.forEach((placeholder) => {
			message = this.parseMessagePlaceholder(placeholder, message);
		});

		return message;
	}

	/**
	 * Adds an individual recipient to the mass notification recipients list.
	 * @param {IntegrationTemplateContact} user - The user to be added.
	 * @param {MassNotificationRecipient[]} recipients - The list of recipients.
	 */
	private addIndividualRecipient(user: IntegrationTemplateContact, recipients: MassNotificationRecipient[]): void {
		recipients.push({
			Fullname: user.fullName,
			ExternalID: user.externalId,
			Email: user.email,
			Telephone: user.telephone,
		});
	}

	/**
	 * Adds a contact group members to the mass notification recipients list.
	 * @param {IntegrationTemplateContact} contactGroup - The contact group whose members are to be added.
	 * @param {MassNotificationRecipient[]} recipients - The list of recipients.
	 */
	private addGroupRecipients(contactGroup: IntegrationTemplateContact, recipients: MassNotificationRecipient[]): void {
		const usersForContactGroup = this.contactGroupMembers.filter(
			(f) => f.contactGroupIds.includes(contactGroup.contactGroupId)
		);

		usersForContactGroup.forEach((user: ContactGroupMembership) => {
			recipients.push({
				Fullname: user.fullName,
				Email: user.email,
				ExternalID: null,
				Telephone: null,
			});
		});
	}

	/**
	 * Sends the notification to the recipients via the device service.
	 * @returns {Promise<void>}
	 */
	private async sendNotification(): Promise<void> {

		if (!this.isSending && !this.$v.template.$error && !this.$v.notificationRecipients.$error) {
			this.isSending = true;

			const deviceService = new DeviceService(api.getDeviceServiceAddress(), this.eventDetails?.devicesEndpoint);
			const authSessionID = this.getSession(SessionResource.DeviceServiceSession);

			let recipients: MassNotificationRecipient[] = [];

			this.notificationRecipients.forEach((contact: IntegrationTemplateContact) => {
				if (contact.contactType != this.IntegrationContactGroup) {
					this.addIndividualRecipient(contact, recipients);
				} else {
					this.addGroupRecipients(contact, recipients);
				}
			});

			const message = {
				Content: this.replaceKeywords(this.template.message),
				Subject: this.replaceKeywords(this.template.subject),
				Parameters: this.template.parameters,
			};

			try {
				await this.createAuditRecord({
					eventId: this.eventDetails.eventID,
					eventRecordTypeId: EventRecordTypes.NotificationEvent,
					details: `Sending notification to Server with ID ${this.serverId}`,
					objectId: this.serverId,
				});

				await deviceService.sendNotification(authSessionID, this.serverId, recipients, message);

				this.$emit("complete");

				await this.createAuditRecord({
					eventId: this.eventDetails.eventID,
					eventRecordTypeId: 41,
					details: `Completed sending notification to Server with ID ${this.serverId}`,
					objectId: this.serverId,
				});
			} catch (error) {
				alert("There has been an error sending this notification. Please try again later or contact support.");
				await this.createAuditRecord({
					eventId: this.eventDetails.eventID,
					eventRecordTypeId: 41,
					details: `Failed sending notification to Server with ID ${this.serverId}`,
					objectId: this.serverId,
				});
			}

			this.isSending = false;
		}
	}

	/**
	 * Creates a placeholder object for a manually added contact. This option is later then validated using validateSelection to provide the user with the option of manually adding a valid email address.
	 * @param {string} newOption - The new contact option.
	 * @returns {IntegrationTemplateContact}
	 */
	private createOption(newOption: string): IntegrationTemplateContact {
		const newContact: IntegrationTemplateContact = {
			email: newOption,
			fullName: newOption,
			contactType: IntegrationTemplateContactType.Adhoc,
			default: false,
			externalId: null,
			telephone: null,
			userId: null,
			contactGroupId: null,
		};

		this.$emit("option:created", newContact);
		return newContact;
	}

	/**
	 * Gets the label for the contact option. This is used internally by v-select-3 to perform filtering.
	 * @param {IntegrationTemplateContact} option - The contact option.
	 * @returns {string}
	 */
	private getOptionLabel(option: IntegrationTemplateContact): string {
		let userDisplay = option.fullName;

		if (option.email && option.email != "") {
			userDisplay += ` (${option.email})`;
		}

		return userDisplay;
	}

	/**
	 * Validates the selected contact option to be an email address or not.
	 * @param {any} option - The contact option.
	 * @returns {boolean}
	 */
	private validateSelection(option: any): boolean {
		if (option.contactType == this.IntegrationContactGroup || option.contactType == this.IntegrationContact) {
			return true;
		} else {
			return !!String(option.email)
				.toLowerCase()
				.match(emailRegex);
		}
	}

	/**
	 * Handles the search input for template contacts.
	 * @param {string} searchTerm - The search term for contacts.
	 * @returns {Promise<void>}
	 */
	private async handleTemplateContactSearchInput(searchTerm: string): Promise<void> {
		if (!searchTerm || searchTerm.length < 1) {
			return;
		}
		this.searchedContacts = undefined;
		await this.templateContactSearch(searchTerm);
	}

	/**
	 * Searches for template contacts based on the query.
	 * @param {string} query - The search query.
	 * @returns {Promise<void>}
	 */
	private templateContactSearch: (query: string) => Promise<void> = debounce(
		async function (query: string): Promise<void> {
			if (this.templateContactSearchToken) {
				this.templateContactSearchToken.cancel("Cancel Search");
			}

			this.templateContactSearchToken = axios.CancelToken.source();
			this.isSearching = true;
			try {

				let tempSearchOptions: IntegrationTemplateContact[] = [];

				const { data } = await api.eventShareSearch(query);
				const contactsMapped: IntegrationTemplateContact[] = data.map((shareWithPerson: ShareWithPerson) => {
					return {
						userId: shareWithPerson.userID,
						fullName: shareWithPerson.name,
						email: shareWithPerson.email,
						contactType: IntegrationTemplateContactType.Contact,
					} as IntegrationTemplateContact;
				});

				contactsMapped.forEach((g) => {
					tempSearchOptions.push(g);
				});

				if (this.isContactGroupsEnabled) {
					const contactGroups: IntegrationTemplateContact[] = await this.retrieveContactGroups(query);
					contactGroups.forEach((g) => {
						tempSearchOptions.push(g);
					});
				}

				this.isSearching = false;

				this.searchedContacts = tempSearchOptions.filter(
					(x) => {
						if (x.contactType == IntegrationTemplateContactType.Contact) {
							return !this.templateUsers.map((y: IntegrationTemplateContact) => y.userId).includes(x.userId);
						} else {
							return !this.templateUsers.map((y: IntegrationTemplateContact) => y.contactGroupId).includes(x.contactGroupId);
						}
					}
				);

				return this.searchedContacts;
			} catch (e) {
				this.isSearching = false;
			}
		},
		this.debouncePeriod,
		{ leading: false, trailing: true }
	);

	/**
	 * Gets the tooltip for the recipient based on its contact type.
	 * @param {IntegrationTemplateContact} user - The recipient user.
	 * @returns {string}
	 */
	private recipientToolTip(user: IntegrationTemplateContact): string {
		let tooltip = "";
		if (user.contactType == IntegrationTemplateContactType.ContactGroup) {
			tooltip += `Contact Group: `;
		}

		tooltip += `${user.fullName} `;

		if (user.email) {
			tooltip += ` - ${user.email}`;
		}
		return tooltip;
	}

	/**
	 * Handles the selection of a contact option and retrieves contact group members for a selected contact group.
	 * @param {any} selectedOption - The selected contact option.
	 * @returns {Promise<void>}
	 */
	private async handleSelected(selectedOption: IntegrationTemplateContact[]): Promise<void> {
		if (this.isContactGroupsEnabled) {
			// get the selected option - newest will be last in the array
			this.selectedItem = selectedOption.slice(-1)[0];

			const params: PaginatedSearchQueryParams = {
				page: 1,
				pageSize: 199,
			};

			this.contactGroupSearchToken = axios.CancelToken.source();

			const request: PaginatedSearchRequest = {
				params: params,
				cancelToken: this.contactGroupSearchToken.token,
			};

			// get the contact group members for the selected contact group
			const contactGroupMembersResult = (await api.getContactGroupMembers(request, this.selectedItem.contactGroupId)).data;

			// map the contact group members to the correct format
			const contactGroupMembers: ContactGroupMembership[] = contactGroupMembersResult.map((x : any) => ({
						contactGroupIds: x.contactGroupId, // Note a single contact group id is returned
						email: x.email,
						fullName: x.fullName,
						userId: x.userId,
					}));

			// add the contact group members to the contact group members list
			this.contactGroupMembers = [...this.contactGroupMembers, ...contactGroupMembers];
		}
	}

	/**
	 * Gets the members of a contact group based on the contact group id.
	 * @param {IntegrationTemplateContact} contactGroup - The contact group.
	 * @returns {ContactGroupMembership[]}
	 */
	private getContactGroupMembers(contactGroup: IntegrationTemplateContact): ContactGroupMembership[] {
		return this.contactGroupMembers.filter((f) => f.contactGroupIds.includes(contactGroup.contactGroupId));
	}

	/**
	 * Gets the count of members in a contact group.
	 * @param {IntegrationTemplateContact} contactGroup - The contact group.
	 * @returns {number}
	 */
	private contactGroupMembersCount(contactGroup: IntegrationTemplateContact): number {
		return this.getContactGroupMembers(contactGroup).length;
	}

	/**
	 * The available message placeholders that can be replaced.
	 */
	private availablePlaceholders = ["{SiteName}", "{EventName}", "{EventTime}", "{EventID}"];

	/**
	 * Parses a message placeholder and replaces it with the actual value.
	 * @param {string} key - The placeholder key.
	 * @param {string} message - The message to be parsed.
	 * @returns {string}
	 */
	private parseMessagePlaceholder(key: string, message: string): string {
		switch (key) {
			case "{SiteName}":
				return message.replace("{SiteName}", this.eventDetails.siteTitle);
			case "{EventName}":
				return message.replace("{EventName}", this.eventDetails.eventTitle);
			case "{EventTime}":
				return message.replace("{EventTime}", displayDateFromISOString(this.eventDetails.created));
			case "{EventID}":
				return message.replace("{EventID}", this.eventDetails.eventID.toString());
			default:
				return message;
		}
	}

	/**
	 * Toggles the advanced sending options.
	 * @param {boolean} state - The visible state of the advanced sending options.
	 */
	private toggleAdvancedSendingOptions(state: boolean): void {
		this.showAdvanced = state;
	}

	private get totalRecipients(): number {
		const templateUsersCount = this.templateUsers?.filter(f => f.contactType == this.IntegrationContact).length || 0;
		const contactGroupMembersCount = this.contactGroupMembers?.length || 0;
		return templateUsersCount + contactGroupMembersCount;
	}
}
